import React, { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { kr, dato } from '../lib/format';
import { PageHeader, Card, Modal, Tabs } from '../components/ui';
import {
  Plus, Pencil, Trash2, Plane, CheckCircle2, XCircle, Send,
  CreditCard, Paperclip, Receipt, Car, FileText,
} from 'lucide-react';

const idag = () => new Date().toISOString().slice(0, 10);

const STATUS = {
  utkast:    { tekst: 'Utkast',    farge: '#6b7280' },
  innsendt:  { tekst: 'Innsendt',  farge: '#3b82f6' },
  godkjent:  { tekst: 'Godkjent',  farge: '#0ea5e9' },
  utbetalt:  { tekst: 'Utbetalt',  farge: '#16a34a' },
  avvist:    { tekst: 'Avvist',    farge: '#dc2626' },
};

const Reiseregninger = () => {
  const [liste, setListe] = useState(null);
  const [statusFilter, setStatusFilter] = useState('alle');
  const [prosjekter, setProsjekter] = useState([]);
  const [open, setOpen] = useState(false);
  const [redigerer, setRedigerer] = useState(null);
  const [skjema, setSkjema] = useState({});
  const [detalj, setDetalj] = useState(null);  // åpen reiseregning m/linjer
  const [utbOpen, setUtbOpen] = useState(null);
  const [utbDato, setUtbDato] = useState(idag());

  const last = useCallback(() => {
    api.get('/reiseregning', { params: { status: statusFilter } })
       .then((r) => setListe(r.data));
  }, [statusFilter]);

  useEffect(() => { last(); }, [last]);
  useEffect(() => {
    api.get('/prosjekt').then((r) => setProsjekter(r.data)).catch(() => {});
  }, []);

  const lastDetalj = (id) => api.get(`/reiseregning/${id}`).then((r) => setDetalj(r.data));

  const aapneNy = () => {
    setRedigerer(null);
    setSkjema({
      tittel: '', formaal: '', destinasjon: '',
      fra_dato: idag(), til_dato: idag(),
      prosjekt_id: '', notater: '',
    });
    setOpen(true);
  };
  const aapneRediger = (r) => {
    setRedigerer(r);
    setSkjema({
      tittel: r.tittel, formaal: r.formaal || '', destinasjon: r.destinasjon || '',
      fra_dato: r.fra_dato, til_dato: r.til_dato,
      prosjekt_id: r.prosjekt_id || '', notater: r.notater || '',
    });
    setOpen(true);
  };

  const lagre = async () => {
    if (!skjema.tittel || !skjema.fra_dato || !skjema.til_dato) {
      alert('Tittel og datoer er påkrevd.');
      return;
    }
    const payload = { ...skjema, prosjekt_id: skjema.prosjekt_id || null };
    try {
      let nyId;
      if (redigerer) {
        await api.put(`/reiseregning/${redigerer.id}`, payload);
        nyId = redigerer.id;
      } else {
        const r = await api.post('/reiseregning', payload);
        nyId = r.data.id;
      }
      setOpen(false); last();
      lastDetalj(nyId);
    } catch (e) {
      alert(e.response?.data?.error || 'Klarte ikke lagre.');
    }
  };

  const slett = async (r) => {
    if (!window.confirm(`Slette «${r.tittel}»?`)) return;
    try {
      await api.delete(`/reiseregning/${r.id}`);
      if (detalj?.id === r.id) setDetalj(null);
      last();
    } catch (e) { alert(e.response?.data?.error || 'Feilet.'); }
  };

  const utforHandling = async (r, sti) => {
    try { await api.post(`/reiseregning/${r.id}/${sti}`); last(); lastDetalj(r.id); }
    catch (e) { alert(e.response?.data?.error || 'Feilet.'); }
  };

  const aapneUtbetal = (r) => { setUtbOpen(r); setUtbDato(idag()); };
  const bekreftUtbetal = async () => {
    try {
      await api.post(`/reiseregning/${utbOpen.id}/utbetal`, { dato: utbDato });
      setUtbOpen(null); last(); lastDetalj(utbOpen.id);
    } catch (e) { alert(e.response?.data?.error || 'Feilet.'); }
  };

  if (!liste) return <span className="fm-text-muted">Laster…</span>;

  const utestaaende = liste
    .filter((r) => ['innsendt', 'godkjent'].includes(r.status))
    .reduce((s, r) => s + (r.sum_total || 0), 0);

  return (
    <>
      <PageHeader
        title="Reiseregninger"
        subtitle="Utlegg, kvitteringer og kjøregodtgjørelse — bokføres ved utbetaling"
        actions={<button className="btn-primary" onClick={aapneNy}><Plus size={14} /> Ny reiseregning</button>}
      />

      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 12 }}>
        <Card style={{ padding: 14 }}>
          <div className="fm-text-muted" style={{ fontSize: 11, textTransform: 'uppercase' }}>Til utbetaling</div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{kr(utestaaende)}</div>
        </Card>
      </div>

      <Tabs
        value={statusFilter}
        onChange={setStatusFilter}
        items={['alle', 'utkast', 'innsendt', 'godkjent', 'utbetalt', 'avvist'].map((s) => ({
          key: s,
          label: s === 'alle' ? 'Alle' : (STATUS[s]?.tekst || s),
        }))}
      />

      <div className="grid" style={{ gridTemplateColumns: detalj ? '1fr 1.3fr' : '1fr', gap: 12 }}>
        <Card style={{ padding: 0 }}>
          {liste.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center' }}>
              <Plane size={32} className="fm-text-muted" style={{ marginBottom: 8 }} />
              <p className="fm-text-muted" style={{ margin: 0, fontSize: 13 }}>Ingen reiseregninger.</p>
            </div>
          ) : (
            <table className="fm-table">
              <thead>
                <tr>
                  <th>Tittel</th>
                  <th>Periode</th>
                  <th>Sum</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {liste.map((r) => {
                  const sb = STATUS[r.status] || { tekst: r.status, farge: '#666' };
                  const aktiv = detalj?.id === r.id;
                  return (
                    <tr key={r.id}
                        style={{ background: aktiv ? 'var(--theme-bg-input)' : undefined, cursor: 'pointer' }}
                        onClick={() => lastDetalj(r.id)}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{r.tittel}</div>
                        {r.bruker_navn && (
                          <div className="fm-text-muted" style={{ fontSize: 11 }}>{r.bruker_navn}</div>
                        )}
                      </td>
                      <td style={{ fontSize: 12 }}>
                        {dato(r.fra_dato)}<br />– {dato(r.til_dato)}
                      </td>
                      <td style={{ fontWeight: 600 }}>{kr(r.sum_total)}</td>
                      <td>
                        <span style={{
                          background: sb.farge, color: '#fff', padding: '2px 8px',
                          borderRadius: 10, fontSize: 11, fontWeight: 600,
                        }}>{sb.tekst}</span>
                      </td>
                      <td onClick={(e) => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                        {r.status !== 'utbetalt' && (
                          <button className="btn-ghost" title="Rediger" onClick={() => aapneRediger(r)}>
                            <Pencil size={14} />
                          </button>
                        )}
                        {!r.bilag_id && (
                          <button className="btn-ghost" title="Slett" onClick={() => slett(r)}>
                            <Trash2 size={14} style={{ color: 'var(--theme-danger)' }} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Card>

        {detalj && (
          <DetaljPanel
            r={detalj}
            prosjekter={prosjekter}
            onLukk={() => setDetalj(null)}
            onOppdater={() => { lastDetalj(detalj.id); last(); }}
            onSendInn={() => utforHandling(detalj, 'sende-inn')}
            onGodkjenn={() => utforHandling(detalj, 'godkjenn')}
            onAvvis={() => utforHandling(detalj, 'avvis')}
            onUtbetal={() => aapneUtbetal(detalj)}
          />
        )}
      </div>

      <Modal open={open} onClose={() => setOpen(false)}
             title={redigerer ? 'Rediger reiseregning' : 'Ny reiseregning'}
             width={620}
             footer={<>
               <button className="btn-ghost" onClick={() => setOpen(false)}>Avbryt</button>
               <button className="btn-primary" onClick={lagre}>Lagre</button>
             </>}>
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label>Tittel *</label>
            <input value={skjema.tittel || ''}
                   onChange={(e) => setSkjema({ ...skjema, tittel: e.target.value })} />
          </div>
          <div>
            <label>Fra dato *</label>
            <input type="date" value={skjema.fra_dato || ''}
                   onChange={(e) => setSkjema({ ...skjema, fra_dato: e.target.value })} />
          </div>
          <div>
            <label>Til dato *</label>
            <input type="date" value={skjema.til_dato || ''}
                   onChange={(e) => setSkjema({ ...skjema, til_dato: e.target.value })} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label>Destinasjon</label>
            <input value={skjema.destinasjon || ''}
                   onChange={(e) => setSkjema({ ...skjema, destinasjon: e.target.value })} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label>Formål</label>
            <textarea rows={2} value={skjema.formaal || ''}
                      onChange={(e) => setSkjema({ ...skjema, formaal: e.target.value })} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label>Prosjekt</label>
            <select value={skjema.prosjekt_id || ''}
                    onChange={(e) => setSkjema({ ...skjema, prosjekt_id: e.target.value })}>
              <option value="">— ingen —</option>
              {prosjekter.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.kode ? `${p.kode} · ` : ''}{p.navn}
                </option>
              ))}
            </select>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label>Notater</label>
            <textarea rows={2} value={skjema.notater || ''}
                      onChange={(e) => setSkjema({ ...skjema, notater: e.target.value })} />
          </div>
        </div>
      </Modal>

      <Modal open={!!utbOpen} onClose={() => setUtbOpen(null)}
             title="Utbetal og bokfør"
             footer={<>
               <button className="btn-ghost" onClick={() => setUtbOpen(null)}>Avbryt</button>
               <button className="btn-primary" onClick={bekreftUtbetal}>Bokfør</button>
             </>}>
        {utbOpen && (
          <div>
            <p style={{ marginTop: 0 }}>
              «{utbOpen.tittel}» (<b>{kr(utbOpen.sum_total)}</b>) blir bokført som
              utgiftsbilag (motkonto bank) og markert utbetalt.
            </p>
            <label>Utbetalingsdato</label>
            <input type="date" value={utbDato} onChange={(e) => setUtbDato(e.target.value)} />
          </div>
        )}
      </Modal>
    </>
  );
};

const DetaljPanel = ({ r, prosjekter, onLukk, onOppdater,
                       onSendInn, onGodkjenn, onAvvis, onUtbetal }) => {
  const sb = STATUS[r.status] || { tekst: r.status, farge: '#666' };
  return (
    <Card style={{ padding: 14 }}>
      <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 15 }}>{r.tittel}</h3>
          <div className="fm-text-muted" style={{ fontSize: 12 }}>
            {r.destinasjon && `${r.destinasjon} · `}
            {dato(r.fra_dato)} – {dato(r.til_dato)}
            {r.prosjekt_navn && ` · ${r.prosjekt_navn}`}
          </div>
        </div>
        <div className="flex items-center" style={{ gap: 6 }}>
          <span style={{
            background: sb.farge, color: '#fff', padding: '2px 8px',
            borderRadius: 10, fontSize: 11, fontWeight: 600,
          }}>{sb.tekst}</span>
          <button className="btn-ghost" onClick={onLukk}>Lukk</button>
        </div>
      </div>

      <div className="flex items-center" style={{ gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {r.status === 'utkast' && (
          <button className="btn-ghost" onClick={onSendInn} style={{ fontSize: 12 }}>
            <Send size={12} /> Send inn
          </button>
        )}
        {(r.status === 'innsendt' || r.status === 'utkast') && (
          <button className="btn-ghost" onClick={onGodkjenn} style={{ fontSize: 12, color: 'var(--theme-success)' }}>
            <CheckCircle2 size={12} /> Godkjenn
          </button>
        )}
        {r.status === 'godkjent' && (
          <button className="btn-primary" onClick={onUtbetal} style={{ fontSize: 12 }}>
            <CreditCard size={12} /> Utbetal & bokfør
          </button>
        )}
        {r.status !== 'utbetalt' && r.status !== 'avvist' && (
          <button className="btn-ghost" onClick={onAvvis} style={{ fontSize: 12, color: 'var(--theme-warning)' }}>
            <XCircle size={12} /> Avvis
          </button>
        )}
        {r.bilag_id && (
          <span className="fm-text-muted" style={{ fontSize: 12 }}>
            <FileText size={12} /> Bilag #{r.bilag_id}
          </span>
        )}
      </div>

      {r.formaal && (
        <div style={{ marginBottom: 12, padding: 8, background: 'var(--theme-bg-input)', borderRadius: 4, fontSize: 12 }}>
          <b>Formål:</b> {r.formaal}
        </div>
      )}

      <UtleggListe r={r} laast={r.status === 'utbetalt'} onOppdater={onOppdater} />
      <KjoringListe r={r} laast={r.status === 'utbetalt'} onOppdater={onOppdater} />

      <div style={{ marginTop: 12, paddingTop: 12, borderTop: '2px solid var(--theme-border)' }}>
        <div className="flex justify-between" style={{ fontSize: 12 }}>
          <span>Utlegg:</span><span>{kr(r.sum_utlegg)}</span>
        </div>
        <div className="flex justify-between" style={{ fontSize: 12 }}>
          <span>Kjøring:</span><span>{kr(r.sum_kjoring)}</span>
        </div>
        <div className="flex justify-between" style={{ fontSize: 14, fontWeight: 700, marginTop: 4 }}>
          <span>Sum:</span><span>{kr(r.sum_total)}</span>
        </div>
      </div>
    </Card>
  );
};

const tomtUtlegg = () => ({
  dato: idag(), beskrivelse: '', kategori: 'Reise',
  belop_eks_mva: '', mva_sats: 25, mva_belop: '', belop_ink_mva: '',
  valuta: 'NOK', kurs: 1,
});

const UtleggListe = ({ r, laast, onOppdater }) => {
  const [open, setOpen] = useState(false);
  const [skjema, setSkjema] = useState(tomtUtlegg());
  const [redigerer, setRedigerer] = useState(null);

  const aapneNy = () => { setRedigerer(null); setSkjema(tomtUtlegg()); setOpen(true); };
  const aapneRed = (u) => { setRedigerer(u); setSkjema({ ...u }); setOpen(true); };

  const oppdaterSum = (s) => {
    const eks = parseFloat(s.belop_eks_mva) || 0;
    const sats = parseFloat(s.mva_sats) || 0;
    const mva = +(eks * sats / 100).toFixed(2);
    const ink = +(eks + mva).toFixed(2);
    return { ...s, mva_belop: mva, belop_ink_mva: ink };
  };
  const sett = (felt, v) => setSkjema((s) => {
    const ny = { ...s, [felt]: v };
    if (['belop_eks_mva', 'mva_sats'].includes(felt)) return oppdaterSum(ny);
    return ny;
  });

  const lagre = async () => {
    const payload = {
      ...skjema,
      belop_eks_mva: parseFloat(skjema.belop_eks_mva) || 0,
      mva_sats: parseFloat(skjema.mva_sats) || 0,
      mva_belop: parseFloat(skjema.mva_belop) || 0,
      belop_ink_mva: parseFloat(skjema.belop_ink_mva) || 0,
    };
    try {
      if (redigerer) await api.put(`/reiseregning/utlegg/${redigerer.id}`, payload);
      else await api.post(`/reiseregning/${r.id}/utlegg`, payload);
      setOpen(false); onOppdater();
    } catch (e) { alert(e.response?.data?.error || 'Feilet.'); }
  };

  const slett = async (u) => {
    if (!window.confirm('Slette utlegg?')) return;
    try { await api.delete(`/reiseregning/utlegg/${u.id}`); onOppdater(); }
    catch (e) { alert(e.response?.data?.error || 'Feilet.'); }
  };

  const lastKvittering = async (u, e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append('fil', f);
    try {
      await api.post(`/reiseregning/utlegg/${u.id}/kvittering`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onOppdater();
    } catch (err) { alert(err.response?.data?.error || 'Opplasting feilet.'); }
    e.target.value = '';
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <div className="flex items-center justify-between" style={{ marginBottom: 6 }}>
        <h4 style={{ fontSize: 12, fontWeight: 600, margin: 0 }}>
          <Receipt size={12} /> Utlegg ({r.utlegg.length})
        </h4>
        {!laast && (
          <button className="btn-ghost" onClick={aapneNy} style={{ fontSize: 11 }}>
            <Plus size={12} /> Nytt utlegg
          </button>
        )}
      </div>
      {r.utlegg.length === 0 && (
        <div className="fm-text-muted" style={{ fontSize: 12, padding: 8 }}>Ingen utlegg.</div>
      )}
      {r.utlegg.map((u) => (
        <div key={u.id} style={{
          padding: '6px 8px', marginBottom: 4, background: 'var(--theme-bg-input)',
          borderRadius: 4, fontSize: 12,
        }}>
          <div className="flex items-center justify-between">
            <div>
              <b>{dato(u.dato)}</b> · {u.beskrivelse || u.kategori}
              <span className="fm-text-muted"> ({u.kategori})</span>
            </div>
            <div className="flex items-center" style={{ gap: 4 }}>
              <span style={{ fontWeight: 600 }}>{kr(u.belop_ink_mva)}</span>
              {u.kvittering && (
                <a href={`/api/reiseregning/utlegg/${u.id}/kvittering`}
                   target="_blank" rel="noreferrer" title={u.kvittering.filnavn}>
                  <Paperclip size={12} />
                </a>
              )}
              {!laast && (
                <>
                  <label className="btn-ghost" style={{ cursor: 'pointer', padding: '0 4px' }}
                         title="Last opp kvittering">
                    <Paperclip size={12} />
                    <input type="file" hidden onChange={(e) => lastKvittering(u, e)} />
                  </label>
                  <button className="btn-ghost" onClick={() => aapneRed(u)} style={{ padding: '0 4px' }}>
                    <Pencil size={12} />
                  </button>
                  <button className="btn-ghost" onClick={() => slett(u)} style={{ padding: '0 4px', color: 'var(--theme-danger)' }}>
                    <Trash2 size={12} />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      ))}

      <Modal open={open} onClose={() => setOpen(false)}
             title={redigerer ? 'Rediger utlegg' : 'Nytt utlegg'}
             footer={<>
               <button className="btn-ghost" onClick={() => setOpen(false)}>Avbryt</button>
               <button className="btn-primary" onClick={lagre}>Lagre</button>
             </>}>
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <label>Dato *</label>
            <input type="date" value={skjema.dato} onChange={(e) => sett('dato', e.target.value)} />
          </div>
          <div>
            <label>Kategori</label>
            <input value={skjema.kategori} onChange={(e) => sett('kategori', e.target.value)} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label>Beskrivelse</label>
            <input value={skjema.beskrivelse} onChange={(e) => sett('beskrivelse', e.target.value)} />
          </div>
          <div>
            <label>Beløp eks. mva</label>
            <input type="number" step="0.01" value={skjema.belop_eks_mva}
                   onChange={(e) => sett('belop_eks_mva', e.target.value)} />
          </div>
          <div>
            <label>MVA-sats (%)</label>
            <input type="number" step="0.01" value={skjema.mva_sats}
                   onChange={(e) => sett('mva_sats', e.target.value)} />
          </div>
          <div>
            <label>MVA-beløp</label>
            <input type="number" step="0.01" value={skjema.mva_belop}
                   onChange={(e) => sett('mva_belop', e.target.value)} />
          </div>
          <div>
            <label>Sum ink. mva</label>
            <input type="number" step="0.01" value={skjema.belop_ink_mva}
                   onChange={(e) => sett('belop_ink_mva', e.target.value)} />
          </div>
          <div>
            <label>Valuta</label>
            <input value={skjema.valuta} onChange={(e) => sett('valuta', e.target.value)} />
          </div>
          <div>
            <label>Kurs</label>
            <input type="number" step="0.0001" value={skjema.kurs}
                   onChange={(e) => sett('kurs', e.target.value)} />
          </div>
        </div>
      </Modal>
    </div>
  );
};

const tomKjoring = () => ({
  dato: idag(), fra_sted: '', til_sted: '', formaal: '',
  antall_km: '', sats_per_km: 4.90,
  antall_passasjerer: 0, passasjer_sats: 1.00,
});

const KjoringListe = ({ r, laast, onOppdater }) => {
  const [open, setOpen] = useState(false);
  const [skjema, setSkjema] = useState(tomKjoring());
  const [redigerer, setRedigerer] = useState(null);

  const aapneNy = () => { setRedigerer(null); setSkjema(tomKjoring()); setOpen(true); };
  const aapneRed = (k) => { setRedigerer(k); setSkjema({ ...k }); setOpen(true); };
  const sett = (felt, v) => setSkjema((s) => ({ ...s, [felt]: v }));

  const lagre = async () => {
    const payload = {
      ...skjema,
      antall_km: parseFloat(skjema.antall_km) || 0,
      sats_per_km: parseFloat(skjema.sats_per_km) || 0,
      antall_passasjerer: parseInt(skjema.antall_passasjerer) || 0,
      passasjer_sats: parseFloat(skjema.passasjer_sats) || 0,
    };
    try {
      if (redigerer) await api.put(`/reiseregning/kjoring/${redigerer.id}`, payload);
      else await api.post(`/reiseregning/${r.id}/kjoring`, payload);
      setOpen(false); onOppdater();
    } catch (e) { alert(e.response?.data?.error || 'Feilet.'); }
  };

  const slett = async (k) => {
    if (!window.confirm('Slette kjøring?')) return;
    try { await api.delete(`/reiseregning/kjoring/${k.id}`); onOppdater(); }
    catch (e) { alert(e.response?.data?.error || 'Feilet.'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between" style={{ marginBottom: 6 }}>
        <h4 style={{ fontSize: 12, fontWeight: 600, margin: 0 }}>
          <Car size={12} /> Kjøring ({r.kjoring.length})
        </h4>
        {!laast && (
          <button className="btn-ghost" onClick={aapneNy} style={{ fontSize: 11 }}>
            <Plus size={12} /> Ny tur
          </button>
        )}
      </div>
      {r.kjoring.length === 0 && (
        <div className="fm-text-muted" style={{ fontSize: 12, padding: 8 }}>Ingen kjøring.</div>
      )}
      {r.kjoring.map((k) => (
        <div key={k.id} style={{
          padding: '6px 8px', marginBottom: 4, background: 'var(--theme-bg-input)',
          borderRadius: 4, fontSize: 12,
        }}>
          <div className="flex items-center justify-between">
            <div>
              <b>{dato(k.dato)}</b> · {k.fra_sted} → {k.til_sted}
              <span className="fm-text-muted"> ({k.antall_km} km × {k.sats_per_km})</span>
            </div>
            <div className="flex items-center" style={{ gap: 4 }}>
              <span style={{ fontWeight: 600 }}>{kr(k.belop)}</span>
              {!laast && (
                <>
                  <button className="btn-ghost" onClick={() => aapneRed(k)} style={{ padding: '0 4px' }}>
                    <Pencil size={12} />
                  </button>
                  <button className="btn-ghost" onClick={() => slett(k)} style={{ padding: '0 4px', color: 'var(--theme-danger)' }}>
                    <Trash2 size={12} />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      ))}

      <Modal open={open} onClose={() => setOpen(false)}
             title={redigerer ? 'Rediger kjøring' : 'Ny kjøring'}
             footer={<>
               <button className="btn-ghost" onClick={() => setOpen(false)}>Avbryt</button>
               <button className="btn-primary" onClick={lagre}>Lagre</button>
             </>}>
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <label>Dato *</label>
            <input type="date" value={skjema.dato} onChange={(e) => sett('dato', e.target.value)} />
          </div>
          <div>
            <label>Antall km</label>
            <input type="number" step="0.1" value={skjema.antall_km}
                   onChange={(e) => sett('antall_km', e.target.value)} />
          </div>
          <div>
            <label>Fra</label>
            <input value={skjema.fra_sted} onChange={(e) => sett('fra_sted', e.target.value)} />
          </div>
          <div>
            <label>Til</label>
            <input value={skjema.til_sted} onChange={(e) => sett('til_sted', e.target.value)} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label>Formål</label>
            <input value={skjema.formaal} onChange={(e) => sett('formaal', e.target.value)} />
          </div>
          <div>
            <label>Sats kr/km</label>
            <input type="number" step="0.01" value={skjema.sats_per_km}
                   onChange={(e) => sett('sats_per_km', e.target.value)} />
          </div>
          <div>
            <label>Antall passasjerer</label>
            <input type="number" value={skjema.antall_passasjerer}
                   onChange={(e) => sett('antall_passasjerer', e.target.value)} />
          </div>
          <div>
            <label>Passasjersats kr/km</label>
            <input type="number" step="0.01" value={skjema.passasjer_sats}
                   onChange={(e) => sett('passasjer_sats', e.target.value)} />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Reiseregninger;
